import { Fragment, useMemo } from 'react'
import Table from 'components/table/Table'
import { useDispatch, useSelector } from 'react-redux'
import NumberFormat from 'react-number-format'
import MultiTable from 'components/table/MultiTable'
import PropTypes from 'prop-types'
import { MetricPresets } from 'features/dashboard/table/shared/Presets'
import produce from 'immer'
import classNames from 'classnames'
import { setUnfold } from 'features/report/reportAddSlice'
import { noop } from 'lodash'
import {
  ReportColumnAssetType,
  ReportColumnDevice,
  ReportColumnTargetingType,
  ReportColumnTemplateCategory,
} from '../common/ReportColumns'
import {
  ReportColumnHour,
  ReportColumnKeyword,
  ReportColumnKeywordEx,
  ReportColumnMediaType,
} from 'features/report/common/ReportColumns'
import useEmptyTableMessage from 'features/report/hooks/useEmptyTableMessage'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import moment from 'moment'
import { ReportAssetTypes } from '../common/ReportAssetTypes'
import { MetricColumns } from '../../Columns'

const ReportScrollableTable = ({
  caption,
  onIsScrolledChange = noop,
  baseColumns = [],
}) => {
  const dispatch = useDispatch()
  const {
    isLoading,
    reports,
    unfold,
    formValues: {
      metricsTypes,
      analysisItem,
      analysisPeriod,
      period: {
        specific: { start, end },
      },
    },
  } = useSelector(state => state.report.reportAdd)
  const emptyMessage = useEmptyTableMessage()
  const metricColumns = useMemo(() => {
    const columns = []
    if (metricsTypes.includes('defaults'))
      columns.push(...MetricPresets.BASIC.columns)
    if (metricsTypes.includes('additional'))
      columns.push(...MetricPresets.ADDITIONAL.columns)
    if (metricsTypes.includes('pixelAndSdk'))
      columns.push(...MetricPresets.PIXEL_AND_SDK.columns)
    if (metricsTypes.includes('kakaoFriend'))
      columns.push(...MetricPresets.TALK_CHANNEL.columns)
    return columns
  }, [metricsTypes])

  const onUnfold = index => () => {
    unfold.includes(index)
      ? dispatch(
          setUnfold(
            produce(unfold, draft => {
              const i = draft.findIndex(item => item === index)
              if (i !== -1) draft.splice(i, 1)
            })
          )
        )
      : dispatch(
          setUnfold(
            produce(unfold, draft => {
              draft.push(index)
            })
          )
        )
  }

  const endDate = (dt, periodUnit) => {
    if (periodUnit === 'day') return dt
    if (periodUnit === 'week')
      return moment(dt).add(6, 'days').format(moment.HTML5_FMT.DATE)
    if (periodUnit === 'month')
      return moment(dt).endOf('month').format(moment.HTML5_FMT.DATE)
  }

  return (
    <div
      className="inner_table"
      onScroll={e => onIsScrolledChange(e.target.scrollLeft > 0)}>
      <MultiTable
        className="tbl_g"
        caption={caption}
        loading={isLoading}
        emptyMessage={emptyMessage}>
        <Table.Head>
          {baseColumns.map((col, index) => (
            <Table.Column
              key={index}
              title={col.head.title}
              minWidth={col.head.minWidth}
            />
          ))}
          {analysisItem === 'mediaType' && (
            <Table.Column
              title={ReportColumnMediaType.head.title}
              minWidth={ReportColumnMediaType.head.minWidth}
            />
          )}
          {analysisItem === 'targetingType' && (
            <Table.Column
              title={ReportColumnTargetingType.head.title}
              minWidth={ReportColumnTargetingType.head.minWidth}
            />
          )}
          {analysisItem === 'device' && (
            <Table.Column
              title={ReportColumnDevice.head.title}
              minWidth={ReportColumnDevice.head.minWidth}
            />
          )}
          {analysisItem === 'keywordEx' && (
            <Table.Column
              title={ReportColumnKeywordEx.head.title}
              minWidth={ReportColumnKeywordEx.head.minWidth}
            />
          )}
          {analysisItem === 'hour' && (
            <Table.Column
              title={ReportColumnHour.head.title}
              minWidth={ReportColumnHour.head.minWidth}
            />
          )}
          {analysisItem === 'assetType' && (
            <Table.Column
              title={ReportColumnAssetType.head.title}
              minWidth={ReportColumnAssetType.head.minWidth}
            />
          )}
          {analysisItem === 'templateCategory' && (
            <Table.Column
              title={ReportColumnTemplateCategory.head.title}
              minWidth={ReportColumnTemplateCategory.head.minWidth}
            />
          )}
          {metricColumns.map((col, index) => (
            <Table.Column
              key={index}
              title={
                <>
                  {col.displayName}
                  {col.name === 'rank' && (
                    <HelpTooltip>
                      조회 단위를 키워드로 선택한 프리미엄링크만 제공합니다.
                    </HelpTooltip>
                  )}
                  {col.name === 'costPerImp' && (
                    <HelpTooltip>
                      톡채널검색만 제공하며,비용을 노출수로 나눈 값을
                      의미합니다. (노출당 비용=비용/노출수)
                    </HelpTooltip>
                  )}
                  {col.name === 'spending' && (
                    <HelpTooltip>{MetricColumns.spending.tooltip}</HelpTooltip>
                  )}
                </>
              }
              minWidth={113}
            />
          ))}
        </Table.Head>
        <Table.Body>
          {reports.map((report, index) => (
            <Fragment key={index}>
              <Table.Row rowId={index} hoverClassName="over">
                {baseColumns.map((col, columnIndex) => (
                  <col.render
                    key={columnIndex}
                    text={report[col.name]}
                    title={report[col.name]}
                    deleted={report[col.deleted]}
                    depthIcon={
                      col.name === 'endDate' && report.splits?.length > 0
                    }
                    unfold={!unfold.includes(index)}
                    onUnfold={onUnfold(index)}
                  />
                ))}
                {analysisItem === 'mediaType' && (
                  <ReportColumnMediaType.render />
                )}
                {analysisItem === 'targetingType' && (
                  <ReportColumnTargetingType.render />
                )}
                {analysisItem === 'device' && <ReportColumnDevice.render />}
                {analysisItem === 'keywordEx' && (
                  <ReportColumnKeywordEx.render />
                )}
                {analysisItem === 'hour' && <ReportColumnHour.render />}
                {analysisItem === 'assetType' && (
                  <ReportColumnAssetType.render />
                )}
                {analysisItem === 'templateCategory' && (
                  <ReportColumnTemplateCategory.render />
                )}
                {metricColumns.map((c, columnIndex) => (
                  <td key={columnIndex} className="align_r">
                    <div className="inner_tbl">
                      {analysisItem !== 'assetType' && (
                        <NumberFormat
                          value={report[c.name] || 0}
                          displayType="text"
                          suffix={c.suffix}
                          thousandSeparator
                        />
                      )}
                    </div>
                  </td>
                ))}
              </Table.Row>
              {report.splits.map((split, innerIndex) => (
                <Fragment key={innerIndex}>
                  <Table.Row
                    rowId={`inner${innerIndex}`}
                    className={classNames('tr_depthitem', {
                      hide: !unfold.includes(index),
                    })}>
                    {baseColumns.map(col => (
                      <Fragment key={col.name}>
                        {col.name === 'startDate' && (
                          <col.render
                            key={col.name}
                            text={split.dateId || start}
                          />
                        )}
                        {col.name === 'endDate' && (
                          <col.render
                            key={col.name}
                            text={
                              split.dateId
                                ? endDate(split.dateId, analysisPeriod)
                                : end
                            }
                          />
                        )}
                        {col.name !== 'startDate' && col.name !== 'endDate' && (
                          <td />
                        )}
                      </Fragment>
                    ))}
                    {analysisItem === 'mediaType' && (
                      <ReportColumnMediaType.render
                        text={split.mediaType}
                        placementSide={split.placementSide}
                      />
                    )}
                    {analysisItem === 'targetingType' && (
                      <ReportColumnTargetingType.render
                        text={split.targetingType}
                      />
                    )}
                    {analysisItem === 'device' && (
                      <ReportColumnDevice.render text={split.userDevice} />
                    )}
                    {analysisItem === 'keywordEx' && (
                      <ReportColumnKeyword.render text={split.userQuery} />
                    )}
                    {analysisItem === 'hour' && (
                      <ReportColumnHour.render text={split.hour?.toString()} />
                    )}
                    {analysisItem === 'assetType' && (
                      <ReportColumnAssetType.render
                        text={ReportAssetTypes[split.assetType]}
                      />
                    )}
                    {analysisItem === 'templateCategory' && (
                      <ReportColumnTemplateCategory.render
                        text={split.templateCategory}
                      />
                    )}
                    {metricColumns.map((c, columnIndex) => (
                      <td key={columnIndex} className="align_r">
                        <div className="inner_tbl">{split[c.name]}</div>
                      </td>
                    ))}
                  </Table.Row>
                </Fragment>
              ))}
            </Fragment>
          ))}
        </Table.Body>
      </MultiTable>
    </div>
  )
}

ReportScrollableTable.propTypes = {
  onIsScrolledChange: PropTypes.func,
  caption: PropTypes.string.isRequired,
  baseColumns: PropTypes.array.isRequired,
}

export default ReportScrollableTable
